const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKDAY_ABBRS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

interface CalendarOptions {
  selectedDate?: string;
}

interface CalendarCallbacks {
  onSelect: (dateValue: string) => void;
  onCancel: () => void;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toIso(year: number, month: number, day: number): string {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

function renderCalendarGrid(
  year: number,
  month: number,
  selectedDate?: string,
): string {
  const today = new Date().toISOString().slice(0, 10);
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  let html = "";

  // Leading days from previous month
  for (let i = firstDay - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const iso = toIso(prevYear, prevMonth, day);
    html += `<button type="button" class="lcars-calendar__day lcars-calendar__day--outside" data-date="${iso}">${day}</button>`;
  }

  // Current month days
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = toIso(year, month, day);
    let cls = "lcars-calendar__day";
    if (iso === today) cls += " lcars-calendar__day--today";
    if (iso === selectedDate) cls += " lcars-calendar__day--selected";
    html += `<button type="button" class="${cls}" data-date="${iso}">${day}</button>`;
  }

  // Trailing days from next month
  const totalCells = firstDay + daysInMonth;
  const trailingCount = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
  for (let day = 1; day <= trailingCount; day++) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const iso = toIso(nextYear, nextMonth, day);
    html += `<button type="button" class="lcars-calendar__day lcars-calendar__day--outside" data-date="${iso}">${day}</button>`;
  }

  return html;
}

export function renderCalendar(options: CalendarOptions): string {
  const { selectedDate } = options;
  let viewYear: number;
  let viewMonth: number;

  if (selectedDate) {
    const parts = selectedDate.split("-");
    viewYear = Number(parts[0]);
    viewMonth = Number(parts[1]) - 1;
  } else {
    const now = new Date();
    viewYear = now.getFullYear();
    viewMonth = now.getMonth();
  }

  const weekdayHeaders = WEEKDAY_ABBRS.map(
    (d) => `<span>${d}</span>`,
  ).join("");

  return `
    <div class="lcars-calendar-overlay">
      <div class="lcars-calendar" data-view-year="${viewYear}" data-view-month="${viewMonth}" data-selected="${selectedDate ?? ""}">
        <div class="lcars-calendar__header">
          <button type="button" class="lcars-calendar__nav lcars-calendar__nav--prev">\u25C0</button>
          <span class="lcars-calendar__month-year">${MONTH_NAMES[viewMonth]} ${viewYear}</span>
          <button type="button" class="lcars-calendar__nav lcars-calendar__nav--next">\u25B6</button>
        </div>
        <div class="lcars-calendar__weekdays">${weekdayHeaders}</div>
        <div class="lcars-calendar__grid">
          ${renderCalendarGrid(viewYear, viewMonth, selectedDate)}
        </div>

      </div>
    </div>`;
}

export function bindCalendarEvents(
  calendarEl: HTMLElement,
  callbacks: CalendarCallbacks,
): () => void {
  const panel = calendarEl.querySelector<HTMLElement>(".lcars-calendar")!;
  const grid = panel.querySelector<HTMLElement>(".lcars-calendar__grid")!;
  const monthYearLabel = panel.querySelector<HTMLElement>(".lcars-calendar__month-year")!;

  function navigateMonth(delta: number): void {
    let viewMonth = Number(panel.dataset.viewMonth);
    let viewYear = Number(panel.dataset.viewYear);
    viewMonth += delta;
    if (viewMonth < 0) {
      viewMonth = 11;
      viewYear--;
    } else if (viewMonth > 11) {
      viewMonth = 0;
      viewYear++;
    }
    panel.dataset.viewMonth = String(viewMonth);
    panel.dataset.viewYear = String(viewYear);
    monthYearLabel.textContent = `${MONTH_NAMES[viewMonth]} ${viewYear}`;
    grid.innerHTML = renderCalendarGrid(
      viewYear,
      viewMonth,
      panel.dataset.selected || undefined,
    );
  }

  panel
    .querySelector(".lcars-calendar__nav--prev")!
    .addEventListener("click", () => navigateMonth(-1));

  panel
    .querySelector(".lcars-calendar__nav--next")!
    .addEventListener("click", () => navigateMonth(1));

  // Day click immediately selects and dismisses
  grid.addEventListener("click", (e) => {
    const dayBtn = (e.target as HTMLElement).closest<HTMLElement>(
      ".lcars-calendar__day",
    );
    if (!dayBtn) return;
    callbacks.onSelect(dayBtn.dataset.date!);
  });

  // Backdrop click
  calendarEl.addEventListener("click", (e) => {
    if (e.target === calendarEl) {
      callbacks.onCancel();
    }
  });

  // Escape key
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      callbacks.onCancel();
    }
  };
  document.addEventListener("keydown", onKeyDown);

  // Return cleanup function
  return () => {
    document.removeEventListener("keydown", onKeyDown);
  };
}
